/**
 * Interview Plan Agent — contrato de datos.
 *
 * v2 (07/10/2026): es el schema de la spec de TA ("Interview Plan Agent ·
 * Especificación técnica", anexo D `schema.json`) tal cual, para que la guía de
 * referencia de Learning Lead (anexo G `guide.json`) entre sin traducir y el
 * .docx salga igual al de TA. La v1 (30/09) se había armado sin los anexos.
 *
 * El tercer agente de Talent Acquisition: recibe una Job Description ya
 * aprobada y devuelve una guía de entrevista repartida en TRES instancias, de
 * modo que cada una produzca evidencia NUEVA y no se repitan entre sí:
 *
 *   - Talent Acquisition → Learning Agility & Overall Fit (amplitud)
 *   - Hiring Manager     → Tech Fit (profundidad)
 *   - Bar Raiser         → Cultural Fit & Raise the Bar (estándar)
 *
 * La instancia de Existential / We.Care es externa a Winclap y queda fuera de
 * la guía (respuesta de TA, 07/10).
 *
 * El modelo devuelve ESTE objeto y nada más (herramienta forzada). El .docx es
 * una proyección del JSON, nunca al revés.
 */

/** Las tres instancias de la entrevista. El orden es el del proceso. */
export const INTERVIEW_STAGES = ['ta', 'hm', 'br'] as const;
export type InterviewStage = (typeof INTERVIEW_STAGES)[number];

/** Cómo nombra cada instancia el `interview_map` de la spec. */
export const STAGE_CODE = { ta: 'TA', hm: 'HM', br: 'BR' } as const satisfies Record<InterviewStage, string>;
export type StageCode = (typeof STAGE_CODE)[InterviewStage];

/** Prefijo del id de pregunta por instancia: P1…, C1…, V1… (adapter.md). Las de inglés son EN1, EN2. */
export const QUESTION_PREFIX: Record<InterviewStage, string> = { ta: 'P', hm: 'C', br: 'V' };
export const ENGLISH_PREFIX = 'EN';

/** Una pregunta. Misma forma en las tres instancias: un solo render y una sola validación. */
export interface InterviewQuestion {
  /** P1, P2… / C1, C2… / V1, V2…, consecutivos y sin huecos. */
  id: string;
  /** Qué mide. En TA puede anteponer el campo del TA Feedback: "Experience · Trayectoria". */
  dimension: string;
  /** Lo que se le dice al candidato. Sin nombrar la competencia. */
  question: string;
  /** 3 a 6 repreguntas. */
  probes: string[];
  /** 2 a 5 señales de evidencia. Solo para el entrevistador. Puede incluir "Alerta: …". */
  evidence: string[];
}

/** Un bloque de la agenda: "3'", "25'", "5–7'". */
export interface AgendaItem {
  label: string;
  minutes: string;
}

export interface InterviewMapRow {
  stage: StageCode;
  focus: string;
  what_we_need_to_know: string;
}

/** Pregunta del bloque en inglés: solo el texto, se hace en inglés y en vivo. */
export interface EnglishQuestion {
  id: string;
  text: string;
}

/**
 * Bloque en inglés. Va con `required: true` solo si la JD REQUIERE inglés (no
 * si lo menciona como deseable) — eso lo decide la plataforma, no el modelo.
 * Sus preguntas cuentan como preguntas de TA dentro del rango 5–7 (TA, 07/10).
 */
export interface EnglishBlock {
  required: boolean;
  title?: string;
  intro?: string;
  questions?: EnglishQuestion[];
  what_to_evaluate?: string;
  /** Otros idiomas deseables: chequeo breve, no excluyente. */
  other_languages?: string | null;
}

export interface DirectCheck {
  item: string;
  validate: string;
}

/** Una fila de la tabla "TA Feedback Coverage". */
export interface FeedbackCoverageRow {
  /** Nombre del campo del TA Feedback, tal cual el formulario. */
  field: string;
  /** Con qué pregunta o check se cubre: "P1 · P3", "Direct check", "GO / NO GO". */
  source: string;
}

export interface TaStagePlan {
  duration: string;
  objective: string;
  dimensions: string[];
  agenda: AgendaItem[];
  questions: InterviewQuestion[];
  english_block: EnglishBlock;
  direct_checks: DirectCheck[];
  /** Los 12 campos del TA Feedback (obligatorio desde el 07/10). */
  feedback_coverage: FeedbackCoverageRow[];
  handoff_to_hm: string;
}

export interface HmStagePlan {
  duration: string;
  objective: string;
  before_interview: string[];
  capabilities: Array<{ name: string; weight: string }>;
  agenda: AgendaItem[];
  questions: InterviewQuestion[];
  optional_case: { title: string; when_to_use: string; prompt: string; evidence: string[] } | null;
}

export const BR_WEIGHTS = ['Alto', 'Medio', 'Bajo'] as const;

export interface BrStagePlan {
  duration: string;
  objective: string;
  priorities: Array<{ dimension: string; weight: (typeof BR_WEIGHTS)[number]; why: string }>;
  agenda: AgendaItem[];
  questions: InterviewQuestion[];
  handoff: { intro: string; items: Array<{ dimension: string; detail: string }> };
}

/** La guía completa: es exactamente lo que devuelve el modelo. */
export interface InterviewGuide {
  position: string;
  area: string;
  impact_level: string;
  reports_to: string;
  location: string;
  purpose: string;
  interview_map: InterviewMapRow[];
  role_context: string;
  ta: TaStagePlan;
  hm: HmStagePlan;
  br: BrStagePlan;
}

/** De dónde salió la JD. Se guarda con la guía para poder rastrearla. */
export type JdRef =
  | { source: 'intake'; sessionId: string; jdVersionId: string }
  | { source: 'library'; clave: string; version: number };

/**
 * La JD tal como estaba al generar (TA, 07/10): si después la JD cambia, la
 * guía sigue diciendo sobre qué texto se armó.
 */
export interface JdSnapshot {
  titulo: string;
  texto: string;
  /** ¿La JD REQUIERE inglés? Decide si va el bloque en inglés. */
  englishRequired: boolean;
  /** De dónde salió `englishRequired`: los idiomas estructurados del intake o el texto. */
  englishSource: 'intake' | 'texto';
  takenAt: string;
}

/** Un problema encontrado al validar lo que devolvió el modelo. */
export interface InterviewIssue {
  /** `error` dispara reintento; `aviso` se registra y se entrega igual. */
  kind: 'error' | 'aviso';
  rule: string;
  detail: string;
}

export const INTERVIEW_PLAN_STATUSES = ['GENERATING', 'READY', 'FAILED'] as const;
export type InterviewPlanStatus = (typeof INTERVIEW_PLAN_STATUSES)[number];

/** 1 = guías del 30/09 (schema provisorio, no se pueden bajar); 2 = schema de la spec. */
export const INTERVIEW_GUIDE_SCHEMA_VERSION = 2;

/** Una guía guardada. No guarda la conversación con el modelo ni los intentos (TA, 07/10). */
export interface InterviewPlanRecord {
  planId: string;
  status: InterviewPlanStatus;
  jd: JdRef;
  position: string;
  guide: InterviewGuide | null;
  warnings: InterviewIssue[];
  error: string | null;
  createdBy: string;
  createdAt: string;
  ms: number;
  schemaVersion: number;
  jdSnapshot: JdSnapshot | null;
  /**
   * Quiénes entrevistan en esta búsqueda. Ven la guía COMPLETA, pero solo la
   * suya; TA ve todas (TA, 07/10). El HM sale del intake; el Bar Raiser lo
   * asigna TA.
   */
  hmEmail: string | null;
  brEmail: string | null;
}

/** Lo que ve el listado (sin la guía ni el texto de la JD). */
export type InterviewPlanSummary = Omit<InterviewPlanRecord, 'guide' | 'jdSnapshot'> & {
  hasGuide: boolean;
  englishRequired: boolean | null;
};
